/* Blok belgesi — sitenin düzenlenebilir her alanı bu şekilde tarif ediliyor.

   Bir sayfa blok listesidir; bir blok metin, görsel, kart dizisi ya da başka
   blokları taşıyan bir kap olabilir. Yerinde düzenleyici (panel) bu listeyi
   değiştiriyor, oluşturucu (tools/src/blocks) HTML'e çeviriyor. İkisinin
   konuştuğu tek dil bu dosya.

   ── Metin ───────────────────────────────────────────────────

   Her metin alanı `{ tr, en? }`. Şema metni **temizleyerek** kabul ediyor
   (`sanitizeRich`, `sanitizePlain`): belgeye hiçbir yoldan ham HTML girmiyor
   — panelden, API'den, elle düzenlenmiş bir satırdan. EN boşsa site TR'yi
   gösteriyor.

   ── Anahtarlar ──────────────────────────────────────────────

   `key` bugünkü sitenin i18n anahtarı (`work.title`). Taşınan bloklar onu
   korur: şablon aynı `data-i18n` özniteliğini basar, i18n.js'teki EN
   karşılık çalışmaya devam eder. Panelden eklenen blokların `key`'i yok;
   oluşturucu `b.<id>.<alan>` üretir.

   ── Bileşenler ──────────────────────────────────────────────

   `hero`, `coverflow`, `reels`, `contact` ve kartların çeşitleri bugünkü
   sayfanın etkileşimli parçaları. İşaretlemeleri main.js'e bağlı olduğu
   için şablonları sabit; içlerindeki metinler, görseller ve kart dizileri
   düzenlenebilir. */

import { z } from 'zod';
import { safeUrl, sanitizePlain, sanitizeRich } from './sanitize.ts';
import { FONTS, type FontName } from './fonts.ts';

/* ── Temel türler ───────────────────────────────────────── */

export type Rich = { tr: string; en?: string | undefined };

const textMax = z.string().max(20_000);

/** Zengin metin: izinli biçim korunur, gerisi temizlenir. */
export const richSchema: z.ZodType<Rich, z.ZodTypeDef, unknown> = z
  .object({ tr: textMax, en: textMax.optional() })
  .strict()
  .transform((v) => {
    const out: Rich = { tr: sanitizeRich(v.tr) };
    const en = v.en === undefined ? '' : sanitizeRich(v.en);
    if (en) out.en = en;
    return out;
  });

/** Düz metin: hiçbir etiket (düğme yazısı, alt metin, video süresi). */
export const plainSchema: z.ZodType<Rich, z.ZodTypeDef, unknown> = z
  .object({ tr: z.string().max(2000), en: z.string().max(2000).optional() })
  .strict()
  .transform((v) => {
    const out: Rich = { tr: sanitizePlain(v.tr) };
    const en = v.en === undefined ? '' : sanitizePlain(v.en);
    if (en) out.en = en;
    return out;
  });

export const blockId = z.string().regex(/^[a-z0-9][a-z0-9-]{0,47}$/, 'Kimlik küçük harf, rakam ve tire.');
const i18nKey = z.string().regex(/^[A-Za-z0-9.]{1,60}$/);
const hex = z.string().regex(/^#[0-9a-f]{6}$/, 'Renk #rrggbb biçiminde olmalı.');
const photoRef = z.string().regex(/^[a-z0-9][a-z0-9-]{0,63}$/);
/** Bugünkü sitenin kendi dosyaları — arşive taşınana kadar. */
const legacySrc = z.string().regex(/^(assets\/(img|video)\/[\w.-]+|https:\/\/(fastly\.)?picsum\.photos\/[\w/?=&.-]+)$/);
const href = z.string().max(500).refine((v) => safeUrl(v) !== null, 'Bağlantı #, /, https://, mailto: ya da tel: ile başlamalı.');

export const FONT_CHOICES = Object.keys(FONTS) as [FontName, ...FontName[]];

/* ── Stil ───────────────────────────────────────────────── */

export const SPACES = ['none', 's', 'm', 'l', 'xl'] as const;

export const blockStyleSchema = z
  .object({
    align: z.enum(['left', 'center', 'right', 'justify']).optional(),
    color: hex.optional(),
    bg: hex.optional(),
    /** Yazı boyutunun çarpanı. */
    scale: z.number().min(0.5).max(3).optional(),
    font: z.enum(FONT_CHOICES).optional(),
    weight: z.union([z.literal(300), z.literal(400), z.literal(500), z.literal(600), z.literal(700)]).optional(),
    width: z.enum(['narrow', 'text', 'wide', 'full']).optional(),
    padY: z.enum(SPACES).optional(),
    marginY: z.enum(SPACES).optional(),
    hidden: z.boolean().optional(),
    /** Yalnızca bir cihazda göster. */
    only: z.enum(['desktop', 'mobile']).optional()
  })
  .strict();

export type BlockStyle = z.infer<typeof blockStyleSchema>;

/* ── Ortak parçalar ─────────────────────────────────────── */

export type Media = { photo?: string | undefined; src?: string | undefined; alt?: Rich | undefined; width?: number | undefined; height?: number | undefined };

export const mediaSchema: z.ZodType<Media, z.ZodTypeDef, unknown> = z
  .object({
    photo: photoRef.optional(),
    src: legacySrc.optional(),
    alt: plainSchema.optional(),
    width: z.number().int().min(1).max(20_000).optional(),
    height: z.number().int().min(1).max(20_000).optional()
  })
  .strict();

export type LinkButton = { id: string; key?: string | undefined; label: Rich; href: string; variant: 'primary' | 'ghost' | 'plain'; arrow?: boolean | undefined };

const linkButtonSchema: z.ZodType<LinkButton, z.ZodTypeDef, unknown> = z
  .object({
    id: blockId,
    key: i18nKey.optional(),
    label: plainSchema,
    href,
    /* `plain`: çerçevesiz `btn` (404'teki ikincil bağlantılar). */
    variant: z.enum(['primary', 'ghost', 'plain']),
    arrow: z.boolean().optional()
  })
  .strict();

export type ListItem = { text: Rich; items?: ListItem[] | undefined };

const listItemSchema: z.ZodType<ListItem, z.ZodTypeDef, unknown> = z.lazy(() =>
  z.object({ text: richSchema, items: z.array(listItemSchema).max(100).optional() }).strict()
);

export type Card = {
  id: string;
  key?: string | undefined;
  media?: Media | undefined;
  /** Büyütme katmanının adresi (yapay zekâ kartları). */
  full?: string | undefined;
  category?: Rich | undefined;
  year?: string | undefined;
  title: Rich;
  desc?: Rich | undefined;
  meta?: Rich | undefined;
  href?: string | undefined;
  /** Başlık Türkçe metinde de İngilizce bir sözcükse ("Editorial") — ekran okuyucu için. */
  titleLang?: 'en' | undefined;
};

const cardSchema: z.ZodType<Card, z.ZodTypeDef, unknown> = z
  .object({
    id: blockId,
    key: i18nKey.optional(),
    media: mediaSchema.optional(),
    full: legacySrc.optional(),
    category: plainSchema.optional(),
    year: z.string().max(20).optional(),
    title: richSchema,
    desc: richSchema.optional(),
    meta: richSchema.optional(),
    href: href.optional(),
    titleLang: z.literal('en').optional()
  })
  .strict();

export type Reel = {
  id: string;
  key?: string | undefined;
  title: Rich;
  spec: Rich;
  note: Rich;
  time: string;
  poster?: string | undefined;
  video?: string | undefined;
  /** Kesitin odak noktası, `--fx/--fy` (ör. "51% 55%"). */
  focus?: string | undefined;
  label?: Rich | undefined;
};

const reelSchema: z.ZodType<Reel, z.ZodTypeDef, unknown> = z
  .object({
    id: blockId,
    key: i18nKey.optional(),
    title: plainSchema,
    spec: plainSchema,
    note: richSchema,
    time: z.string().regex(/^\d{1,2}:\d{2}$/),
    poster: legacySrc.optional(),
    video: legacySrc.optional(),
    focus: z.string().regex(/^\d{1,3}% \d{1,3}%$/).optional(),
    label: plainSchema.optional()
  })
  .strict();

/** `valueKey`: değer de çevriliyorsa onun anahtarı (iletişimdeki "Temsil"). */
export type Fact = { id: string; key?: string | undefined; valueKey?: string | undefined; label: Rich; value: Rich };

const factSchema: z.ZodType<Fact, z.ZodTypeDef, unknown> = z
  .object({ id: blockId, key: i18nKey.optional(), valueKey: i18nKey.optional(), label: plainSchema, value: richSchema })
  .strict();

export type HeroLine = { id: string; text: string; indent?: boolean | undefined; amp?: boolean | undefined };

const heroLineSchema: z.ZodType<HeroLine, z.ZodTypeDef, unknown> = z
  .object({
    id: blockId,
    text: z.string().max(40).transform((v) => sanitizePlain(v)),
    indent: z.boolean().optional(),
    amp: z.boolean().optional()
  })
  .strict();

/** Menü ve footer bağlantısı. `#çapa` ana sayfadaki bir bölüme gider. */
export type NavLink = { id: string; key?: string | undefined; label: Rich; href: string; lang?: 'en' | undefined };

const navLinkSchema: z.ZodType<NavLink, z.ZodTypeDef, unknown> = z
  .object({ id: blockId, key: i18nKey.optional(), label: plainSchema, href, lang: z.literal('en').optional() })
  .strict();

/* ── Bloklar ────────────────────────────────────────────── */

/** `reveal`: kaydırınca belirme animasyonu (style.css `.reveal`). */
type Base = { id: string; key?: string | undefined; style?: BlockStyle | undefined; reveal?: boolean | undefined };

export type Block =
  | (Base & { type: 'heading'; level: 1 | 2 | 3 | 4; text: Rich })
  | (Base & { type: 'paragraph'; text: Rich; variant?: 'lede' | 'lead' | 'body' | 'small' | undefined })
  | (Base & { type: 'eyebrow'; text: Rich })
  | (Base & { type: 'note'; text: Rich; variant?: 'lang' | undefined })
  | (Base & { type: 'quote'; text: Rich; cite?: Rich | undefined })
  | (Base & { type: 'list'; ordered: boolean; items: ListItem[] })
  | (Base & { type: 'table'; header: boolean; rows: Rich[][] })
  | (Base & { type: 'buttons'; items: LinkButton[]; variant?: 'more' | 'plain' | undefined })
  | (Base & { type: 'divider' })
  | (Base & { type: 'spacer'; size: 's' | 'm' | 'l' | 'xl' })
  | (Base & { type: 'image'; media: Media; caption?: Rich | undefined; ratio?: 'auto' | '1:1' | '4:5' | '3:2' | '16:9' | undefined; bleed?: boolean | undefined })
  | (Base & { type: 'gallery'; items: (Media & { id: string })[]; cols: 2 | 3 | 4 })
  | (Base & { type: 'columns'; columns: { id: string; blocks: Block[] }[] })
  | (Base & { type: 'cards'; variant: 'ai' | 'service' | 'plain'; items: Card[] })
  | (Base & { type: 'reels'; items: Reel[] })
  | (Base & { type: 'facts'; variant: 'stats' | 'contact' | 'plain'; items: Fact[] })
  | (Base & { type: 'split'; media: Media; caption?: Rich | undefined; captionKey?: string | undefined; blocks: Block[] })
  | (Base & { type: 'cta'; text: Rich; button: LinkButton })
  | (Base & { type: 'coverflow'; photos: string[]; more?: LinkButton | undefined })
  | (Base & { type: 'contact'; info: Fact[]; note?: Rich | undefined })
  | (Base & {
      type: 'hero';
      eyebrow: Rich;
      lines: HeroLine[];
      lede: Rich;
      buttons: LinkButton[];
      hint?: Rich | undefined;
      media?: Media | undefined;
    })
  | (Base & { type: 'section'; anchor: string; alt: boolean; head: Block[]; blocks: Block[] })
  | (Base & { type: 'page'; head: Block[]; blocks: Block[] })
  | (Base & { type: 'pagecta'; title: Rich; text: Rich; buttons: LinkButton[] })
  | (Base & { type: 'nav'; items: NavLink[]; brandSub: Rich; location: Rich })
  | (Base & { type: 'footer'; mark: Rich; links: NavLink[]; legal: Rich })
  | (Base & { type: 'html'; html: string });

export type BlockType = Block['type'];

const base = { id: blockId, key: i18nKey.optional(), style: blockStyleSchema.optional(), reveal: z.boolean().optional() };

export const blockSchema: z.ZodType<Block, z.ZodTypeDef, unknown> = z.lazy(() =>
  z.discriminatedUnion('type', [
    z.object({ ...base, type: z.literal('heading'), level: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]), text: richSchema }).strict(),
    z.object({ ...base, type: z.literal('paragraph'), text: richSchema, variant: z.enum(['lede', 'lead', 'body', 'small']).optional() }).strict(),
    z.object({ ...base, type: z.literal('eyebrow'), text: richSchema }).strict(),
    z.object({ ...base, type: z.literal('note'), text: richSchema, variant: z.literal('lang').optional() }).strict(),
    z.object({ ...base, type: z.literal('quote'), text: richSchema, cite: richSchema.optional() }).strict(),
    z.object({ ...base, type: z.literal('list'), ordered: z.boolean(), items: z.array(listItemSchema).min(1).max(200) }).strict(),
    /* Satırların eşit uzunlukta olması `pageSchema`'da denetleniyor:
       discriminatedUnion `.refine`'lı bir seçeneği kabul etmiyor. */
    z.object({ ...base, type: z.literal('table'), header: z.boolean(), rows: z.array(z.array(richSchema).min(1).max(12)).min(1).max(200) }).strict(),
    z.object({ ...base, type: z.literal('buttons'), items: z.array(linkButtonSchema).min(1).max(6), variant: z.enum(['more', 'plain']).optional() }).strict(),
    z.object({ ...base, type: z.literal('divider') }).strict(),
    z.object({ ...base, type: z.literal('spacer'), size: z.enum(['s', 'm', 'l', 'xl']) }).strict(),
    z
      .object({
        ...base,
        type: z.literal('image'),
        media: mediaSchema,
        caption: richSchema.optional(),
        ratio: z.enum(['auto', '1:1', '4:5', '3:2', '16:9']).optional(),
        bleed: z.boolean().optional()
      })
      .strict(),
    z
      .object({
        ...base,
        type: z.literal('gallery'),
        items: z.array(z.intersection(mediaSchema, z.object({ id: blockId }))).min(1).max(60),
        cols: z.union([z.literal(2), z.literal(3), z.literal(4)])
      })
      .strict(),
    z
      .object({
        ...base,
        type: z.literal('columns'),
        columns: z.array(z.object({ id: blockId, blocks: z.array(blockSchema).max(100) }).strict()).min(2).max(4)
      })
      .strict(),
    z.object({ ...base, type: z.literal('cards'), variant: z.enum(['ai', 'service', 'plain']), items: z.array(cardSchema).max(60) }).strict(),
    z.object({ ...base, type: z.literal('reels'), items: z.array(reelSchema).max(20) }).strict(),
    z.object({ ...base, type: z.literal('facts'), variant: z.enum(['stats', 'contact', 'plain']), items: z.array(factSchema).max(40) }).strict(),
    z
      .object({ ...base, type: z.literal('split'), media: mediaSchema, caption: richSchema.optional(), captionKey: i18nKey.optional(), blocks: z.array(blockSchema).max(100) })
      .strict(),
    z.object({ ...base, type: z.literal('cta'), text: richSchema, button: linkButtonSchema }).strict(),
    z.object({ ...base, type: z.literal('coverflow'), photos: z.array(photoRef).max(40), more: linkButtonSchema.optional() }).strict(),
    z.object({ ...base, type: z.literal('contact'), info: z.array(factSchema).max(20), note: richSchema.optional() }).strict(),
    z
      .object({
        ...base,
        type: z.literal('hero'),
        eyebrow: plainSchema,
        lines: z.array(heroLineSchema).min(1).max(5),
        lede: richSchema,
        buttons: z.array(linkButtonSchema).max(3),
        hint: plainSchema.optional(),
        media: mediaSchema.optional()
      })
      .strict(),
    z
      .object({
        ...base,
        type: z.literal('section'),
        anchor: z.string().regex(/^[a-z][a-z0-9-]{1,30}$/, 'Bölüm kimliği küçük harf, rakam ve tire; harfle başlar.'),
        alt: z.boolean(),
        head: z.array(blockSchema).max(20),
        blocks: z.array(blockSchema).max(200)
      })
      .strict(),
    /* Sayfa bölgesi: works, blog, 404'ün başlık kısmı (`page-head`) ve
       altına eklenen bloklar. */
    z.object({ ...base, type: z.literal('page'), head: z.array(blockSchema).max(20), blocks: z.array(blockSchema).max(200) }).strict(),
    z.object({ ...base, type: z.literal('pagecta'), title: plainSchema, text: richSchema, buttons: z.array(linkButtonSchema).max(4) }).strict(),
    /* Ortak alanlar: her sayfanın üst menüsü (masaüstü ve telefon) ve footer'ı. */
    z.object({ ...base, type: z.literal('nav'), items: z.array(navLinkSchema).max(12), brandSub: plainSchema, location: plainSchema }).strict(),
    z.object({ ...base, type: z.literal('footer'), mark: plainSchema, links: z.array(navLinkSchema).max(12), legal: richSchema }).strict(),
    /* Eski yazıların tanınmayan parçası (taşıma). Düzenlenemez, yalnızca
       silinebilir ya da taşınabilir; içeriği veritabanından geldiği gibi. */
    z.object({ ...base, type: z.literal('html'), html: z.string().max(200_000) }).strict()
  ])
) as unknown as z.ZodType<Block, z.ZodTypeDef, unknown>;

/* ── Ağaç gezintisi ─────────────────────────────────────── */

/** Bir bloğun doğrudan çocuk listeleri — editör ve doğrulama aynı yolu yürüyor. */
export function childLists(block: Block): Block[][] {
  switch (block.type) {
    case 'section':
    case 'page':
      return [block.head, block.blocks];
    case 'columns':
      return block.columns.map((c) => c.blocks);
    case 'split':
      return [block.blocks];
    default:
      return [];
  }
}

/** Her bloğu derinlemesine ziyaret eder. */
export function walk(blocks: Block[], visit: (block: Block, parent: Block[]) => void): void {
  for (const block of blocks) {
    visit(block, blocks);
    for (const list of childLists(block)) walk(list, visit);
  }
}

/** Bloktaki ve kart/öge dizilerindeki bütün kimlikler. */
export function allIds(blocks: Block[]): string[] {
  const ids: string[] = [];
  walk(blocks, (b) => {
    ids.push(b.id);
    if (b.type === 'cards' || b.type === 'reels' || b.type === 'facts' || b.type === 'gallery') ids.push(...b.items.map((i) => i.id));
    if (b.type === 'contact') ids.push(...b.info.map((i) => i.id));
    if (b.type === 'columns') ids.push(...b.columns.map((c) => c.id));
    if (b.type === 'buttons') ids.push(...b.items.map((i) => i.id));
    if (b.type === 'hero') ids.push(...b.lines.map((l) => l.id), ...b.buttons.map((x) => x.id));
    if (b.type === 'cta') ids.push(b.button.id);
    if (b.type === 'pagecta') ids.push(...b.buttons.map((x) => x.id));
    if (b.type === 'nav') ids.push(...b.items.map((x) => x.id));
    if (b.type === 'footer') ids.push(...b.links.map((x) => x.id));
    if (b.type === 'coverflow' && b.more) ids.push(b.more.id);
  });
  return ids;
}

/** Bir sayfanın blok listesi: kimlikler ve bölüm çapaları tekil. */
export const pageSchema = z
  .array(blockSchema)
  .max(60)
  .superRefine((blocks, ctx) => {
    const seen = new Set<string>();
    for (const id of allIds(blocks)) {
      if (seen.has(id)) ctx.addIssue({ code: 'custom', message: `"${id}" kimliği iki kez kullanılmış.` });
      seen.add(id);
    }
    const anchors = new Set<string>();
    walk(blocks, (b) => {
      if (b.type === 'table' && !b.rows.every((r) => r.length === b.rows[0]!.length)) {
        ctx.addIssue({ code: 'custom', message: 'Tablonun bütün satırları aynı sayıda hücre taşımalı.' });
      }
      if (b.type !== 'section') return;
      if (anchors.has(b.anchor)) ctx.addIssue({ code: 'custom', message: `"#${b.anchor}" bölüm kimliği iki kez kullanılmış.` });
      anchors.add(b.anchor);
    });
  });

/** Yeni blok kimliği: kısa, okunabilir, sayfada tekil. */
export function newId(prefix: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  const clean = prefix.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 20) || 'b';
  for (let n = 1; ; n += 1) {
    const id = `${clean}-${n}`;
    if (!used.has(id)) return id;
  }
}

/**
 * Yerinden oynamayan bloklar: menü, footer ve sayfa bölgesi. Editör bunları
 * taşımıyor, çoğaltmıyor, silmiyor — yalnızca içlerini düzenletiyor.
 */
export function isFixed(block: Block): boolean {
  return block.type === 'nav' || block.type === 'footer' || block.type === 'page';
}

/**
 * Bir yazının gövdesine girmeyen tipler: sayfa iskeleti ve ana sayfanın
 * bileşenleri. Yazı `post.html`'in `<article>`'ı içinde basılıyor; bölüm,
 * menü ya da iletişim formu orada anlamsız (ve form sayfada ikinci kez
 * görünürdü).
 */
export const NOT_IN_POSTS: readonly BlockType[] = ['section', 'page', 'nav', 'footer', 'hero', 'coverflow', 'contact', 'reels', 'pagecta'];

/** Yazı gövdesi: sayfa kuralları + yalnızca içerik blokları. */
export const postBodySchema = pageSchema.superRefine((blocks, ctx) => {
  walk(blocks, (b) => {
    if (NOT_IN_POSTS.includes(b.type)) ctx.addIssue({ code: 'custom', message: `"${b.type}" bloğu yazı gövdesine eklenemez.` });
  });
});
